import * as formik from 'formik';
import * as fabric from 'office-ui-fabric-react';
import { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import * as api from '../../../../../api/generated';
import { KendoLoading } from '../../../../../components';
import LocationSelectionFieldWindow from '../../../../../components/custom/LocationSelectionFieldWindow';
import { ILocationItem } from '../../../../../components/custom/LocationTree';
import { IWindowComponentProps, useWindow } from '../../../../../components/windows/WindowContext';
import * as enums from '../../../../../enums';
import * as models from '../../../../../models';

interface IDoorScreenCreateModalProps {
    refetchDoorScreens(): Promise<void>;
    basedOnInitialValues: models.IDoorScreenListItem | null;
    openDoorScreenCreateModalWindow: (basedOnInitialValues?: models.IDoorScreenListItem | null) => Promise<void>;
}

const validationSchema = Yup.object<models.IDoorScreenListItem>().shape({
    name: Yup.string().required('Šis laukas yra privalomas'),
    model: Yup.string().required('Šis laukas yra privalomas'),
    screenSoundType: Yup.number().required('Šis laukas yra privalomas')
});

const initialState: models.IDoorScreenListItem = {
    id: 0,
    name: '',
    model: '',
    locationId: null,
    screenSoundType: enums.screenSoundType.None,
    displayType: enums.doorScreenDisplayType.Initial,
    locationName: '',
    connectionStatus: false
};

const DoorScreenCreateModalWindow: FC<IWindowComponentProps<IDoorScreenCreateModalProps>> = (props) => {
    const {
        componentProps: { refetchDoorScreens, openDoorScreenCreateModalWindow, basedOnInitialValues },
        onClose
    } = props;

    const { openWindow } = useWindow();
    const [isLoading, setIsLoading] = useState(false);
    const [createNext, setCreateNext] = useState<boolean>(false);
    const [selectedLocationName, setSelectedLocationName] = useState<string | null>(null);

    const fetchData = async () => {
        try {
            setIsLoading(true);
        } catch (ex) {
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (values: models.IDoorScreenListItem, formikActions: formik.FormikHelpers<models.IDoorScreenListItem>) => {
        try {
            setIsLoading(true);
            formikActions.setStatus();
            await api.administration.createDoorScreen(values);
            toast.success('Ekranas sėkmingai sukurtas');
            await refetchDoorScreens();
            onClose();
            if (createNext) {
                await openDoorScreenCreateModalWindow(values);
            }
        } catch (ex) {
            formikActions.setStatus(ex);
            toast.error(ex.error[0]);
        } finally {
            formikActions.setSubmitting(false);
            setIsLoading(false);
        }
    };
    const openLocationSelectionFieldWindow = async (form: formik.FormikProps<any>, field: formik.FieldInputProps<models.IDoorScreen>) => {
        try {
            LocationSelectionFieldWindow.defaultProps = {
                componentProps: {
                    onSelect: (locationItem: ILocationItem) => {
                        form.setFieldValue(field.name, locationItem.id);
                        setSelectedLocationName(locationItem.name);
                    }
                }
            };
            openWindow({
                title: 'Įrenginio lokacijos pasirinkimas',
                component: LocationSelectionFieldWindow,
                isDialogBehaviour: true,
                width: 800,
                height: 400
            });
        } catch (ex) {
            toast.error(ex.error[0]);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    return (
        <>
            {isLoading ? (
                <KendoLoading />
            ) : (
                <>
                    <formik.Formik
                        validationSchema={validationSchema}
                        initialValues={basedOnInitialValues === null ? initialState : { ...basedOnInitialValues, name: '' }}
                        onSubmit={handleSubmit}
                        render={(props) => (
                            <formik.Form>
                                <div className="ms-Grid-row">
                                    <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                        <formik.Field
                                            name="name"
                                            render={({ field, form }: formik.FieldProps<string>) => (
                                                <fabric.TextField
                                                    {...field}
                                                    label="Pavadinimas"
                                                    placeholder="Įrašykite durų ekrano pavadinimą"
                                                    errorMessage={(props.status && props.status.name) || (form.errors.name && form.errors.name)}
                                                />
                                            )}
                                        />
                                    </div>
                                    <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                        <formik.Field name="model">
                                            {({ field, form }: formik.FieldProps<string>) => (
                                                <fabric.TextField
                                                    {...field}
                                                    label="Modelis"
                                                    placeholder="Įrašykite info ekrano modelį"
                                                    errorMessage={(props.status && props.status.model) || (form.errors.model && form.errors.model)}
                                                />
                                            )}
                                        </formik.Field>
                                    </div>
                                </div>
                                <div className="ms-Grid-row">
                                    <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                        <formik.Field
                                            name="locationId"
                                            render={({ field, form }: formik.FieldProps<models.IDoorScreen>) => (
                                                <>
                                                    <fabric.Label>Lokacijos</fabric.Label>

                                                    <fabric.Text variant="xLarge">
                                                        <fabric.Link onClick={() => openLocationSelectionFieldWindow(form, field)}>
                                                            {selectedLocationName ? selectedLocationName : 'Pasirinkite įrenginio lokaciją'}
                                                        </fabric.Link>
                                                        {selectedLocationName && (
                                                            <fabric.IconButton
                                                                style={{ color: fabric.getTheme().semanticColors.errorText }}
                                                                onClick={() => {
                                                                    form.setFieldValue(field.name, null);
                                                                    setSelectedLocationName(null);
                                                                }}
                                                                iconProps={{ iconName: 'ChromeClose' }}
                                                            />
                                                        )}
                                                    </fabric.Text>
                                                </>
                                            )}
                                        />
                                    </div>
                                    <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                        <formik.Field
                                            name="screenSoundType"
                                            render={({ field, form }: formik.FieldProps<enums.ScreenSoundType>) => (
                                                <fabric.Dropdown
                                                    defaultSelectedKey={field.value}
                                                    {...field}
                                                    onChange={({}, option?: fabric.IDropdownOption) =>
                                                        option && form.setFieldValue(field.name, option.key)
                                                    }
                                                    label="Skambėjimo tipas"
                                                    options={enums.screenSoundType.displayTypesArray()}
                                                    errorMessage={
                                                        (props.status && props.status.screenSoundType) ||
                                                        (form.errors.screenSoundType && form.errors.screenSoundType)
                                                    }
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                                <div className="ms-Grid-row" dir="ltr">
                                    <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                        <formik.Field
                                            name="displayType"
                                            render={({ field, form }: formik.FieldProps<enums.DoorScreenDisplayType>) => (
                                                <fabric.Dropdown
                                                    defaultSelectedKey={field.value}
                                                    {...field}
                                                    onChange={({}, option?: fabric.IDropdownOption) => {
                                                        option && form.setFieldValue(field.name, option.key);
                                                    }}
                                                    label="Atvaizdavimo tipas"
                                                    options={enums.doorScreenDisplayType.doorScreenDisplayTypesArray()}
                                                    errorMessage={
                                                        (props.status && props.status.displayType) ||
                                                        (form.errors.displayType && form.errors.displayType)
                                                    }
                                                />
                                            )}
                                        />
                                    </div>
                                    <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                        <formik.Field
                                            name="url"
                                            render={() => (
                                                <>
                                                    <fabric.Label style={{ marginBottom: '5px' }}>Nuoroda į įrenginio rodomą vaizdą</fabric.Label>
                                                    <fabric.Link disabled>Nuoroda nepasiekiama kol įrenginys nėra sukurtas</fabric.Link>
                                                </>
                                            )}
                                        />
                                    </div>
                                </div>

                                <div style={{ textAlign: 'center', marginTop: '30px' }}>
                                    <fabric.Checkbox
                                        label="Kurti kitą"
                                        checked={createNext}
                                        onChange={() => setCreateNext(!createNext)}
                                    ></fabric.Checkbox>
                                    <fabric.PrimaryButton
                                        className="ms-slideRightIn40 ms-textAlignRight"
                                        text="Išsaugoti"
                                        disabled={props.isSubmitting || !props.isValid || !props.dirty}
                                        type="submit"
                                    />
                                </div>
                            </formik.Form>
                        )}
                    />
                </>
            )}
        </>
    );
};

export default DoorScreenCreateModalWindow;
